import { useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { BRANDS, BRAND_INFO } from '@hub/shared'
import { brandFill } from './components/Brand.js'
import { Icon } from './components/Icon.js'
import type { IconName } from './components/Icon.js'
import { ItemSheet } from './components/ItemSheet.js'
import { NewItemSheet } from './components/NewItemSheet.js'
import { isLoggedIn } from './lib/api.js'
import { useSheetParam } from './lib/filters.js'
import { Calendar } from './screens/Calendar.js'
import { Inspiration } from './screens/Inspiration.js'
import { Performance } from './screens/Performance.js'
import { Settings } from './screens/Settings.js'
import { Login } from './screens/Login.js'
import { Overview } from './screens/Overview.js'
import { Pipeline } from './screens/Pipeline.js'

/** `tab`: shown in the phone's tab bar (five at most, HIG). The rest is reached from the overview. */
const SECTIONS: { to: string; label: string; short?: string; icon: IconName; tab: boolean }[] = [
  { to: '/', label: 'Übersicht', icon: 'home', tab: true },
  { to: '/pipeline', label: 'Pipeline', icon: 'pipeline', tab: true },
  { to: '/kalender', label: 'Kalender', icon: 'calendar', tab: true },
  { to: '/inspiration', label: 'Inspiration', short: 'Ideen', icon: 'spark', tab: true },
  { to: '/performance', label: 'Performance', short: 'Zahlen', icon: 'chart', tab: true },
]

/** Keeps the filters when switching sections; drops an open sheet. */
function useNavSearch(): string {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.delete('eintrag')
  params.delete('neu')
  const s = params.toString()
  return s ? `?${s}` : ''
}

function Shell({ onLogout }: { onLogout: () => void }): JSX.Element {
  const search = useNavSearch()
  const { itemId, creating, newDate, fromIdea, openItem, openNew, close } = useSheetParam()

  const navigate = useNavigate()

  // Keyboard on the Mac: "n" new entry (as in Things or Linear), 1–5 switch section.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement
      if (e.metaKey || e.ctrlKey || e.altKey || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return
      if (document.querySelector('[role="dialog"]')) return
      if (e.key === 'n') {
        e.preventDefault()
        openNew()
      }
      const section = SECTIONS[Number(e.key) - 1]
      if (section) navigate(section.to + search)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openNew, navigate, search])

  return (
    <div className="flex min-h-full bg-grouped">
      {/* Sidebar — tablet and desktop */}
      <nav className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col px-3 py-6 md:flex">
        <div className="flex items-center gap-3 px-3 pb-6">
          <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-[22%]" />
          <div>
            <p className="font-brand text-callout font-normal leading-tight">Marketing Hub</p>
            <div className="mt-1 flex gap-1" aria-label="Marken">
              {BRANDS.map((b) => (
                <span key={b} title={BRAND_INFO[b].name} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: brandFill(b) }} />
              ))}
            </div>
          </div>
        </div>
        <button type="button" onClick={() => openNew()} className="btn-primary mx-1 mb-4 min-h-[42px] text-callout">
          <Icon name="plus" size={18} /> Neuer Eintrag
        </button>
        <div className="space-y-0.5">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.to}
              to={s.to + search}
              end
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-[12px] px-3 py-2 text-callout transition-colors ${
                  isActive ? 'bg-cell font-semibold text-label shadow-sm' : 'text-label2/70 hover:bg-fill/[0.08]'
                }`
              }
            >
              <Icon name={s.icon} size={19} />
              {s.label}
            </NavLink>
          ))}
        </div>
        <NavLink
          to={'/verbindungen' + search}
          className={({ isActive }) =>
            `mt-auto flex items-center gap-3 rounded-[12px] px-3 py-2 text-small transition-colors ${isActive ? 'bg-cell font-semibold text-label' : 'text-label2/55 hover:text-label'}`
          }
        >
          <Icon name="claude" size={17} /> Verbindungen
        </NavLink>
      </nav>

      <main className="min-w-0 flex-1 pb-navbar md:pb-0">
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/kalender" element={<Calendar />} />
          <Route path="/inspiration" element={<Inspiration />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/verbindungen" element={<Settings onLogout={onLogout} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Tab bar — phone */}
      <nav className="glass fixed inset-x-3 z-30 flex items-center justify-around rounded-full px-2 py-1.5 md:hidden" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}>
        {SECTIONS.filter((s) => s.tab).map((s) => (
          <NavLink
            key={s.to}
            to={s.to + search}
            end
            className={({ isActive }) =>
              `flex min-w-[56px] flex-col items-center gap-0.5 rounded-full px-2 py-1 text-[10px] font-medium ${isActive ? 'text-pebro' : 'text-label2/60'}`
            }
          >
            <Icon name={s.icon} size={22} />
            {s.short ?? s.label}
          </NavLink>
        ))}
      </nav>
      <button
        type="button"
        onClick={() => openNew()}
        aria-label="Neuer Eintrag"
        className="glass-prominent fixed right-5 z-30 grid h-14 w-14 place-items-center rounded-full md:hidden"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)' }}
      >
        <Icon name="plus" size={26} />
      </button>

      {itemId && <ItemSheet id={itemId} onClose={close} />}
      {creating && <NewItemSheet date={newDate} ideaId={fromIdea} onClose={close} onCreated={openItem} />}
    </div>
  )
}

export function App(): JSX.Element {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn())
  useEffect(() => {
    const onExpired = (): void => setLoggedIn(false)
    window.addEventListener('hub:session-expired', onExpired)
    return () => window.removeEventListener('hub:session-expired', onExpired)
  }, [])
  if (!loggedIn) return <Login onDone={() => setLoggedIn(true)} />
  return <Shell onLogout={() => setLoggedIn(false)} />
}
